import { ApiClient, ApiConflictError, ApiOfflineError, ApiRequestError, CredentialStore } from "./ApiClient";
import type { SyncCredentials } from "./ApiClient";
import type { GardenStorage } from "./Storage";
import { withChecksum } from "./Storage";
import type { GardenSummary } from "./ApiClient";
import type { SaveData } from "../types";

/**
 * ローカル保存とサーバー保存の調停役。
 *
 * 原則:
 *   1. localStorage は「即座に再開するためのキャッシュ」、サーバーは「デバイス間の正」。
 *   2. サーバーが落ちていても、未設定でも、ゲームは止まらない。同期は best-effort。
 *   3. 衝突時は進行度で勝者を決め、負けた側は必ず退避スロットへ残す。黙って消さない。
 */

export type SyncState = "disabled" | "offline" | "idle" | "syncing" | "conflict" | "locked";

export interface SyncSnapshot {
  state: SyncState;
  message?: string;
  lastSyncedAt?: number;
}

export interface BootstrapResult {
  save: SaveData | null;
  /** 起動時にユーザーへ知らせるべきことがあれば入る。 */
  notice?: string;
  sync: SyncSnapshot;
}

/** 変更後にサーバーへ送るまでの待ち時間。ローカル保存(0.8秒)より粗くしてリクエストをまとめる。 */
const PUSH_DEBOUNCE_MS = 12_000;
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;

export class SyncManager {
  private timer = 0;
  private inFlight: Promise<void> | null = null;
  private dirty = false;
  private retryDelay = RETRY_BASE_MS;
  private snapshot: SyncSnapshot = { state: "disabled" };
  private readonly listeners = new Set<(snapshot: SyncSnapshot) => void>();

  constructor(
    private readonly api: ApiClient,
    private readonly credentials: CredentialStore,
    private readonly storage: GardenStorage,
    private readonly getSave: () => SaveData,
  ) {}

  get status(): SyncSnapshot {
    return this.snapshot;
  }

  get enabled(): boolean {
    return this.api.enabled;
  }

  get playerId(): string | null {
    return this.credentials.read()?.playerId ?? null;
  }

  get gardenId(): string | null {
    return this.credentials.read()?.gardenId ?? null;
  }

  get token(): string | null {
    return this.credentials.read()?.token ?? null;
  }

  subscribe(listener: (snapshot: SyncSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setStatus(state: SyncState, message?: string): void {
    this.snapshot = { state, ...(message !== undefined ? { message } : {}), ...(this.snapshot.lastSyncedAt !== undefined ? { lastSyncedAt: this.snapshot.lastSyncedAt } : {}) };
    for (const listener of this.listeners) listener(this.snapshot);
  }

  private markSynced(revision: number): void {
    this.credentials.update({ syncedRevision: revision });
    this.retryDelay = RETRY_BASE_MS;
    this.snapshot = { state: "idle", lastSyncedAt: Date.now() };
    for (const listener of this.listeners) listener(this.snapshot);
  }

  /**
   * 起動時に呼ぶ。ローカルのセーブとサーバーのセーブを突き合わせ、採用する方を返す。
   * サーバーに繋がらない場合はローカルをそのまま返し、以後リトライで追いつく。
   */
  async bootstrap(local: SaveData | null): Promise<BootstrapResult> {
    if (!this.api.enabled) {
      this.setStatus("disabled");
      return { save: local, sync: this.snapshot };
    }
    if (this.storage.isLocked) {
      // 新しいスキーマのローカルセーブを踏んでいる。サーバーへ書き戻すと被害が広がるので何もしない。
      this.setStatus("locked", "新しいバージョンのセーブデータを検出したため、同期を停止しています。");
      return { save: local, notice: "新しいバージョンのセーブデータが見つかりました。データを守るため保存と同期を停止しています。最新のページを再読み込みしてください。", sync: this.snapshot };
    }

    try {
      const credentials = await this.ensureCredentials();
      // やり直した直後は、以前の庭を拾わずに新しい庭を始める。
      const gardenId = credentials.gardenId ?? (credentials.startingNew ? null : await this.discoverGarden(credentials.token));

      if (!gardenId) {
        // サーバー側にまだ庭がない。ローカルがあれば作成し、無ければ初回保存時に作る。
        if (local) {
          const created = await this.api.createGarden(credentials.token, local, "");
          this.credentials.update({ gardenId: created.gardenId, syncedRevision: created.revision, startingNew: false });
          this.markSynced(created.revision);
          return { save: local, sync: this.snapshot };
        }
        this.setStatus("idle");
        return { save: null, sync: this.snapshot };
      }

      const remote = await this.api.getGarden(credentials.token, gardenId);
      this.credentials.update({ gardenId });
      return this.resolveBootstrap(local, remote.state, remote.revision, credentials.syncedRevision);
    } catch (error) {
      if (error instanceof ApiOfflineError) {
        this.setStatus("offline", error.message);
        return { save: local, sync: this.snapshot };
      }
      if (error instanceof ApiRequestError && error.status === 401) {
        // トークンが無効化されている。次回起動で匿名プレイヤーを取り直す。
        this.credentials.clear();
        this.setStatus("offline", "サーバーの認証情報が無効になりました。");
        return { save: local, notice: "サーバーとの接続情報が無効になりました。ローカル保存で続行します。", sync: this.snapshot };
      }
      if (error instanceof ApiRequestError && error.status === 404) {
        // 参照していた庭が消えている。ローカルを正として作り直す。
        this.credentials.update({ gardenId: null, syncedRevision: 0 });
        this.setStatus("idle");
        return { save: local, sync: this.snapshot };
      }
      this.setStatus("offline", error instanceof Error ? error.message : "同期に失敗しました。");
      return { save: local, sync: this.snapshot };
    }
  }

  private resolveBootstrap(local: SaveData | null, remote: SaveData, remoteRevision: number, syncedRevision: number): BootstrapResult {
    if (!local) {
      this.markSynced(remoteRevision);
      return { save: remote, notice: "サーバーに保存されていた庭を読み込みました。", sync: this.snapshot };
    }
    if (remoteRevision === syncedRevision) {
      // サーバーは前回同期した状態のまま。ローカルの続きがそのまま正。
      this.markSynced(remoteRevision);
      return { save: local, sync: this.snapshot };
    }

    // 別デバイスが進めている。進行度（総 tick）が大きい方を採用し、負けた側は退避する。
    const localTicks = local.time?.totalTicks ?? 0;
    const remoteTicks = remote.time?.totalTicks ?? 0;
    const remoteWins = remoteTicks > localTicks || (remoteTicks === localTicks && remote.updatedAt > local.updatedAt);
    if (remoteWins) {
      this.storage.stashConflict(local);
      this.markSynced(remoteRevision);
      return { save: remote, notice: "別のデバイスで進めた庭の方が新しかったため、そちらを読み込みました。", sync: this.snapshot };
    }
    this.storage.stashConflict(remote);
    // ローカルを正として、サーバーの最新 revision を基準に上書きする。
    this.credentials.update({ syncedRevision: remoteRevision });
    this.setStatus("idle");
    this.schedulePush();
    return { save: local, notice: "サーバー側の庭より手元の方が進んでいたため、手元の庭で続行します。", sync: this.snapshot };
  }

  private async ensureCredentials(): Promise<SyncCredentials> {
    const existing = this.credentials.read();
    if (existing) return existing;
    const created = await this.api.createPlayer("");
    const credentials: SyncCredentials = { playerId: created.playerId, token: created.token, gardenId: null, syncedRevision: 0 };
    this.credentials.write(credentials);
    return credentials;
  }

  private async discoverGarden(token: string): Promise<string | null> {
    const { gardens } = await this.api.listGardens(token);
    const newest = gardens[0];
    if (!newest) return null;
    this.credentials.update({ gardenId: newest.gardenId, syncedRevision: 0 });
    return newest.gardenId;
  }

  /**
   * サーバー側の庭を削除し、同期状態を初期化する（「最初からやり直す」用）。
   *
   * サーバーに庭を残したままローカルだけ消すと、次回起動時に bootstrap が
   * それを拾って古い庭を復元してしまう。消せなかった場合は ok:false を返し、
   * 呼び出し側でローカルを消させない。
   */
  async resetGarden(): Promise<{ ok: boolean; message?: string }> {
    const credentials = this.credentials.read();
    if (this.api.enabled && credentials?.gardenId) {
      try {
        await this.api.deleteGarden(credentials.token, credentials.gardenId);
      } catch (error) {
        // すでに存在しないなら目的は達成されている。
        const gone = error instanceof ApiRequestError && error.status === 404;
        if (!gone) {
          return { ok: false, message: error instanceof ApiOfflineError ? "サーバーに接続できないため、やり直しを完了できませんでした。" : "サーバー側の庭を削除できませんでした。" };
        }
      }
    }
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
    this.dirty = false;
    this.credentials.update({ gardenId: null, syncedRevision: 0 });
    this.setStatus(this.api.enabled ? "idle" : "disabled");
    return { ok: true };
  }

  /** この プレイヤーが持つ庭の一覧。 */
  async listGardens(): Promise<GardenSummary[]> {
    const credentials = this.credentials.read();
    if (!this.api.enabled || !credentials) return [];
    const { gardens } = await this.api.listGardens(credentials.token);
    return gardens;
  }

  async renameGarden(gardenId: string, name: string): Promise<void> {
    const credentials = this.credentials.read();
    if (!this.api.enabled || !credentials) return;
    await this.api.renameGarden(credentials.token, gardenId, name);
  }

  /**
   * 別の庭へ切り替える。現在の庭をサーバーへ確定させてから参照先を差し替える。
   * ローカルセーブの破棄と再読み込みは呼び出し側（Game）が行う。
   */
  async switchGarden(gardenId: string): Promise<{ ok: boolean; message?: string }> {
    if (!this.api.enabled) return { ok: false, message: "サーバー同期が無効です。" };
    if (!(await this.pushNow())) return { ok: false, message: "いまの庭をサーバーへ保存できなかったため、切り替えを中止しました。" };
    this.credentials.update({ gardenId, syncedRevision: 0, startingNew: false });
    return { ok: true };
  }

  /**
   * いまの庭を残したまま、新しい庭を始める準備をする。
   * 削除しないので、一覧からいつでも戻れる。
   */
  async startNewGarden(): Promise<{ ok: boolean; message?: string }> {
    if (!this.api.enabled) {
      // サーバー未設定なら、ローカルを消すだけで新規開始になる。
      return { ok: true };
    }
    if (!(await this.pushNow())) return { ok: false, message: "いまの庭をサーバーへ保存できなかったため、中止しました。" };
    this.credentials.update({ gardenId: null, syncedRevision: 0, startingNew: true });
    return { ok: true };
  }

  /**
   * 指定した庭の休止設定を切り替える。
   * 正はセーブ本体（time.paused）なので、サーバーの状態を読んで書き戻す。
   * チェックサムを付け直さないと、中身と食い違ったセーブがサーバーに残る。
   */
  async setGardenPaused(gardenId: string, paused: boolean): Promise<{ ok: boolean; message?: string }> {
    const credentials = this.credentials.read();
    if (!this.api.enabled || !credentials) return { ok: false, message: "サーバー同期が無効です。" };
    try {
      const remote = await this.api.getGarden(credentials.token, gardenId);
      const next = withChecksum({ ...remote.state, time: { ...remote.state.time, paused, lastRealTimestamp: Date.now() } });
      await this.api.putGarden(credentials.token, gardenId, remote.revision, next);
      return { ok: true };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "設定を変更できませんでした。" };
    }
  }

  /** ゲーム側の変更通知。実際の送信はデバウンスされる。 */
  schedulePush(): void {
    if (!this.api.enabled || this.storage.isLocked) return;
    this.dirty = true;
    if (this.timer) return;
    this.timer = window.setTimeout(() => {
      this.timer = 0;
      void this.flush();
    }, PUSH_DEBOUNCE_MS);
  }

  /**
   * 未送信かどうかに関わらず、サーバーへの反映を確定させる。
   * 公開(オープンガーデン)はサーバー保存済みのセーブから投影するため、公開前に必ず通す。
   */
  async pushNow(): Promise<boolean> {
    if (!this.api.enabled || this.storage.isLocked) return false;
    this.dirty = true;
    await this.flush();
    return this.snapshot.state === "idle";
  }

  /** 未送信の変更を今すぐ送る。ページ離脱時は keepalive を使う。 */
  async flush(keepalive = false): Promise<void> {
    if (!this.api.enabled || this.storage.isLocked) return;
    if (this.snapshot.state === "conflict") return;
    if (this.inFlight) return this.inFlight;
    if (!this.dirty && this.snapshot.state !== "offline") return;
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
    this.inFlight = this.push(keepalive).finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async push(keepalive: boolean): Promise<void> {
    const credentials = this.credentials.read();
    if (!credentials) return;
    const save = this.getSave();
    this.dirty = false;
    this.setStatus("syncing");
    try {
      if (!credentials.gardenId) {
        const created = await this.api.createGarden(credentials.token, save, "");
        this.credentials.update({ gardenId: created.gardenId, syncedRevision: created.revision, startingNew: false });
        this.markSynced(created.revision);
        return;
      }
      const result = await this.api.putGarden(credentials.token, credentials.gardenId, credentials.syncedRevision, save, keepalive);
      this.markSynced(result.revision);
    } catch (error) {
      if (error instanceof ApiConflictError) return this.handleConflict(error, save, keepalive, credentials);
      this.dirty = true;
      if (error instanceof ApiOfflineError) {
        this.setStatus("offline", "サーバーに接続できません。ローカル保存で続行しています。");
        this.scheduleRetry();
        return;
      }
      if (error instanceof ApiRequestError && error.status === 401) {
        this.credentials.clear();
        this.setStatus("offline", "サーバーの認証情報が無効になりました。");
        return;
      }
      this.setStatus("offline", error instanceof Error ? error.message : "同期に失敗しました。");
      this.scheduleRetry();
    }
  }

  private async handleConflict(conflict: ApiConflictError, local: SaveData, keepalive: boolean, credentials: SyncCredentials): Promise<void> {
    const localTicks = local.time?.totalTicks ?? 0;
    if (localTicks >= conflict.totalTicks) {
      // 手元の方が進んでいる。サーバーの最新を退避したうえで、その revision を基準に上書きする。
      this.storage.stashConflict(conflict.state);
      this.credentials.update({ syncedRevision: conflict.revision });
      try {
        const gardenId = credentials.gardenId;
        if (!gardenId) return;
        const result = await this.api.putGarden(credentials.token, gardenId, conflict.revision, local, keepalive);
        this.markSynced(result.revision);
      } catch {
        this.dirty = true;
        this.setStatus("offline", "同期の再試行に失敗しました。");
        this.scheduleRetry();
      }
      return;
    }
    // サーバー側が進んでいる。走行中のゲーム状態を差し替えるのは危険なので、
    // 自動送信を止めてユーザーに再読み込みを促す。手元のセーブは退避スロットに残る。
    this.storage.stashConflict(local);
    this.setStatus("conflict", "別のデバイスで庭が進んでいます。再読み込みすると最新の状態になります。");
  }

  private scheduleRetry(): void {
    if (this.timer) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(RETRY_MAX_MS, this.retryDelay * 2);
    this.timer = window.setTimeout(() => {
      this.timer = 0;
      void this.flush();
    }, delay);
  }

  dispose(): void {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = 0;
    this.listeners.clear();
  }
}
